import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { homeSummaryApi, BannerSwapSummary } from "../../../../api/homeSummaryApi";
import { bannerSwapAdminOpsApi } from "../../../../api/bannerSwapAdminOpsApi";
import { ROUTES } from "../../../../config/routes";
import { WidgetComponentProps, getScope, scopeParams } from "../types";
import { useWidgetData } from "../useWidgetData";
import {
  CardError,
  CardLoading,
  Freshness,
  Kpi,
  KpiGrid,
  ListRows,
  countTone,
  formatNumber,
  formatRelative,
  formatShortDate,
} from "../ui";

type BannerCardData = BannerSwapSummary & { adminIssues: number | null };

const BannerSwapCard: React.FC<WidgetComponentProps> = ({ access, filters }) => {
  const windowDays = Number(filters.windowDays) || 7;
  const requestParams = useMemo(
    () => ({
      ...scopeParams(filters),
      windowDays,
      includeManagedChannels: filters.includeManagedChannels !== false,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(filters)]
  );

  const { data, loading, error, reload } = useWidgetData<BannerCardData>(
    `banner-swap:${access.isAdmin}:${JSON.stringify(requestParams)}`,
    async () => {
      const [summary, adminIssues] = await Promise.all([
        homeSummaryApi.getBannerSwap(requestParams),
        access.isAdmin
          ? bannerSwapAdminOpsApi
              .getIssues()
              .then((r) => r.counts.total)
              .catch(() => null)
          : Promise.resolve(null),
      ]);
      return { ...summary, adminIssues };
    }
  );

  if (loading && !data) return <CardLoading />;
  if (error) return <CardError message={error} onRetry={reload} />;
  if (!data) return null;

  const mine = getScope(filters) === "mine";
  const queueLink = (status: string) =>
    `${ROUTES.BANNER_DASHBOARD_SWAP_QUEUE}?status=${status}${mine ? "&mine=true" : ""}`;

  return (
    <>
      <KpiGrid columns={3}>
        <Kpi
          label="Live now"
          value={data.liveNow}
          tone={data.liveNow > 0 ? "info" : "neutral"}
          to={queueLink("running")}
          emphasis
        />
        <Kpi
          label={`Starting in ${windowDays} days`}
          value={data.upcoming}
          tone={data.upcoming > 0 ? "info" : "neutral"}
          to={`${ROUTES.BANNER_DASHBOARD_CALENDAR}${mine ? "?view=my_swaps" : ""}`}
        />
        <Kpi
          label="Failed swaps"
          value={data.needsAttention}
          tone={countTone(data.needsAttention, "bad")}
          hint="Failed or partly failed"
          to={queueLink("failed,partial_failed")}
        />
      </KpiGrid>

      <ListRows
        title="Next up"
        emptyText={`No swaps starting in the next ${windowDays} days`}
        rows={data.nextUpcoming.map((s) => ({
          key: s.swapUID || s.swapName,
          primary: s.swapName || "Untitled swap",
          secondary: `${formatShortDate(s.startDate)} (${formatRelative(s.startDate)}) · ${formatNumber(
            s.channelCount
          )} channel${s.channelCount === 1 ? "" : "s"}`,
          to: `${ROUTES.BANNER_DASHBOARD_SWAP_QUEUE}?status=scheduled${mine ? "&mine=true" : ""}`,
        }))}
      />

      <div className="home-card__meta">
        {data.channelsWithoutDefault > 0 ? (
          <Link
            className="home-link home-text-warn"
            to={`${ROUTES.BANNER_DASHBOARD_DEFAULT_BANNERS}?bannerStates=no_default${mine ? "&myChannels=true" : ""}`}
          >
            <i className="pi pi-image" /> {formatNumber(data.channelsWithoutDefault)} of{" "}
            {formatNumber(data.channelsInScope)} {mine ? "your " : ""}channels have no default banner
          </Link>
        ) : (
          <span className="home-muted">
            <i className="pi pi-check" /> {mine ? "All your channels have" : "Every channel has"} a default banner
          </span>
        )}
        {data.adminIssues != null && (
          <Link
            className={`home-link${data.adminIssues > 0 ? " home-text-bad" : ""}`}
            to={ROUTES.BANNER_SWAP_ADMIN_OPS}
          >
            <i className="pi pi-wrench" /> {formatNumber(data.adminIssues)} ops issue{data.adminIssues === 1 ? "" : "s"}
          </Link>
        )}
        <Freshness at={data.generatedAt} />
      </div>
    </>
  );
};

export default BannerSwapCard;
